import {
    Box,
    Button,
    Checkbox,
    Divider,
    Icon,
    IconButton,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from "@mui/material";
import { SyntheticEvent, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ProjectSearchType } from "../../../api/psc";
import { HelpIconButton } from "../../../components/exports";
import { DialogContext } from "../../../contexts/DialogContext";
import { SnackbarContext } from "../../../contexts/SnackbarContext";
import {
    clearSelectedProjects,
    setSelectedProjects,
} from "../../../slices/SharedSlice";
import { RootState } from "../../../slices/Store";
import TutorialWrapper from "../../tutorialModule/TutorialWrapper";
import BatchUpdateProjectVisibilityDialog from "../dialogs/BatchUpdateProjectVisibilityDialog";
import SelectFolderForBatchDialog from "../dialogs/SelectFolderForBatchDialog";
import PrintSelection from "../dialogs/SelectionPrintDialog";
import SmsCampaignFromProjectSelectionDialog from "../dialogs/SmsCampaignFromProjectSelectionDialog";
import { ProjectState } from "../slice/ProjectSlice";
import { PromotedProjectState } from "../slice/PromotedProjectSlice";

export default function MobileProjectToolbox(props: {
    fetchProjects: () => void;
    setProjectType: (ProjectSearchType) => void;
    filterElement: JSX.Element;
    state: ProjectState | PromotedProjectState;
    isRequirements: boolean;
}) {
    const dispatch = useDispatch();

    const sharedState = useSelector((state: RootState) => state.shared);

    const { showSnackbar } = useContext(SnackbarContext);
    const { openDialog } = useContext(DialogContext);

    const handleTypeChange = (
        event: SyntheticEvent,
        newValue: ProjectSearchType
    ) => {
        props.setProjectType(newValue as ProjectSearchType);
    };
    const openFilterDialog = () => {
        openDialog(props.filterElement);
    };

    const openCustomPrintDialog = () => {
        if (sharedState.batchOperationProjects.length === 0) {
            showSnackbar("ابتدا حداقل یک پروژه انتخاب کنید", "error");
            return;
        }

        openDialog(
            <PrintSelection projects={sharedState.batchOperationProjects} />,
            (printPerformed) => {
                if (printPerformed) {
                    props.fetchProjects();
                }
            }
        );
    };

    const openSelectionSmsCampaignDialog = () => {
        if (sharedState.batchOperationProjects.length === 0) {
            showSnackbar("ابتدا حداقل یک پروژه انتخاب کنید", "error");
            return;
        }
        openDialog(
            <SmsCampaignFromProjectSelectionDialog
                projects={sharedState.batchOperationProjects}
                onSent={() => {
                    dispatch(clearSelectedProjects());
                }}
            />
        );
    };

    const openSelectionFoldersDialog = () => {
        if (sharedState.batchOperationProjects.length === 0) {
            showSnackbar("ابتدا حداقل یک پروژه انتخاب کنید", "error");
            return;
        }
        openDialog(
            <SelectFolderForBatchDialog
                projects={sharedState.batchOperationProjects}
                onMoved={() => {
                    props.fetchProjects();
                }}
            />
        );
    };

    const openDeleteBatchProjectDialog = () => {
        if (sharedState.batchOperationProjects.length === 0) {
            showSnackbar("ابتدا حداقل یک پروژه انتخاب کنید", "error");
            return;
        }
        openDialog(
            <BatchUpdateProjectVisibilityDialog
                projects={sharedState.batchOperationProjects}
                onApplied={() => {
                    props.fetchProjects();
                }}
            />
        );
    };

    const toggleAll = () => {
        if (sharedState.batchOperationProjects.length > 0) {
            dispatch(clearSelectedProjects());
        } else {
            dispatch(setSelectedProjects(props.state.projects));
        }
    };

    return (
        <Stack direction="column" divider={<Divider />}>
            <Stack direction="row">
                <Tabs
                    value={props.state.filter.type}
                    onChange={handleTypeChange}
                    variant="fullWidth"
                    sx={{ flexGrow: 1 }}
                >
                    <Tab value={ProjectSearchType.NEW} label="جدید" />
                    <Tab value={ProjectSearchType.VISITED} label="مشاهده شده" />
                    {!props.isRequirements && (
                        <Tab value={ProjectSearchType.ALL} label="همه" />
                    )}
                </Tabs>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ my: 1 }}>
                <HelpIconButton tag="projects" />

                <Tooltip title="پرینت پروژه ها">
                    <IconButton
                        color="primary"
                        disabled={
                            props.state.filter.type === ProjectSearchType.ALL
                        }
                        onClick={openCustomPrintDialog}
                    >
                        <Icon>print</Icon>
                    </IconButton>
                </Tooltip>

                <Tooltip title="پیامک به سازندگان پروژه ها">
                    <IconButton
                        color="secondary"
                        disabled={
                            props.state.filter.type === ProjectSearchType.ALL
                        }
                        onClick={openSelectionSmsCampaignDialog}
                    >
                        <Icon>sms</Icon>
                    </IconButton>
                </Tooltip>

                <Tooltip title="انتقال پروژه ها به پوشه">
                    <IconButton
                        color="warning"
                        onClick={openSelectionFoldersDialog}
                    >
                        <Icon>folder</Icon>
                    </IconButton>
                </Tooltip>

                <Tooltip title="حذف گروهی پروژه ها">
                    <IconButton
                        color="error"
                        onClick={openDeleteBatchProjectDialog}
                    >
                        <Icon>delete</Icon>
                    </IconButton>
                </Tooltip>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                    variant="text"
                    color="primary"
                    onClick={openFilterDialog}
                    startIcon={<Icon>filter_alt</Icon>}
                >
                    فیلتر ها
                </Button>

                <TutorialWrapper
                    steps={["mobile-signup-3"]}
                    onNextStep={openFilterDialog}
                />
            </Stack>

            <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ my: 1, px: 0.5 }}
            >
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography color="text.secondary" fontSize="smaller">
                        {props.state.totalCount.toLocaleString()}
                    </Typography>

                    <Typography color="text.secondary" fontSize="small">
                        پروژه
                    </Typography>
                </Stack>

                <Stack direction="row" alignItems="center">
                    <Checkbox
                        size="small"
                        checked={
                            sharedState.batchOperationProjects.length ===
                            props.state.projects.length
                        }
                        onChange={toggleAll}
                    />
                    <Typography color="text.secondary" fontSize="small">
                        انتخاب همه
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    );
}
